import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Auth/AuthContext";
import PaymentApiService from '../../ApiServices/PaymentApiServices/PaymentApiService';
import InvoiceApiService from '../../ApiServices/InvoiceApiService/InvoiceApiService';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import PaymentMethodModal from '../Payment/AddPaymentModelForm';

// Payment Method Icons
import {
  Banknote,
  Bitcoin,
  Calendar,
  ChevronRight,
  CheckCircle,
  Plus
} from "lucide-react";
import { FaPaypal } from "react-icons/fa";

const Payment = () => {
  const [data, setData] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("bank");
  const [lastPaidInvoice, setLastPaidInvoice] = useState(null);
  const [lastUnPaidInvoice, setLastUnPaidInvoice] = useState(null);
  const { authState } = useAuth();
  const role = authState.roles;
  const admin = role === "Admin";
  const user = authState.user;
  const navigate = useNavigate();

useEffect(() => {
  AOS.init({ duration: 1000, once: true });

  const fetchData = async () => {
    try {
      if (admin) {
        const paymentData = await PaymentApiService.Get();
        setData(paymentData.data[0] || {});
      } else {
        const paymentData = await PaymentApiService.GetByUser(user.id);
        setData(paymentData.data || {});
        const lastPaidInvoiceData = await InvoiceApiService.GetLastPaidInvoice(user.id);
        const lastUnPaidInvoiceData = await InvoiceApiService.GetLastUnPaidInvoice(user.id);
        setLastPaidInvoice(lastPaidInvoiceData.data);
        setLastUnPaidInvoice(lastUnPaidInvoiceData.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  fetchData();
}, [admin, user.id]);

  const handleShowTransaction = () => navigate("/transactions");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Payment Summary Card */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-xl p-6 mb-8 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div className="mb-4 md:mb-0">
            <h2 className="text-xl font-semibold mb-1">Payment Summary</h2>
            <p className="text-indigo-100">
              Last paid: {lastPaidInvoice?.previousMonth || "N/A"} {lastPaidInvoice?.year || ""}
            </p>
            <p className="text-indigo-100 mt-2">
              Next payment: {
                new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
                  new Date(new Date().getFullYear(), new Date().getMonth() + 2)
                )
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">${lastPaidInvoice ? (lastPaidInvoice.revenue - lastPaidInvoice.deductions).toFixed(2) : "0.00"}</p>
            <div className="flex justify-end space-x-4 mt-1">
              <span className="text-sm text-indigo-100">Revenue: ${lastPaidInvoice?.revenue?.toFixed(2) || "0.00"}</span>
              <span className="text-sm text-indigo-100">Deductions: ${lastPaidInvoice?.deductions?.toFixed(2) || "0.00"}</span>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium">Payment Threshold</span>
            <span className="text-sm font-medium">100%</span>
          </div>
          <div className="w-full bg-indigo-300 rounded-full h-2.5">
            <div
              className="bg-white h-2.5 rounded-full"
              style={{ width: "100%" }}
            ></div>
          </div>
          <p className="text-xs text-indigo-100 mt-1">Minimum payout: $200.00</p>
        </div>
      </div>

      {/* Payment Methods Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Bank Transfer */}
        <div
          className={`border rounded-xl p-5 cursor-pointer transition-all duration-300 ${selectedMethod === "bank" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-indigo-300"}`}
          onClick={() => setSelectedMethod("bank")}
        >
          <div className="flex items-center mb-3">
            <div className={`p-2 rounded-full mr-3 ${selectedMethod === "bank" ? "bg-indigo-100 text-indigo-600" : "bg-gray-100 text-gray-600"}`}>
              <Banknote size={20} />
            </div>
            <h3 className="font-medium">Bank Transfer</h3>
          </div>
          {selectedMethod === "bank" && (
            <div className="space-y-2 text-sm text-gray-600">
              <p><span className="font-medium">Account:</span> {data.bankAccountNumber || "Not set"}</p>
              <p><span className="font-medium">Bank:</span> {data.bankName || "Not set"}</p>
              <p><span className="font-medium">Branch:</span> {data.bankBranch || "Not set"}</p>
              <p><span className="font-medium">SWIFT:</span> {data.swiftCode || "Not set"}</p>
            </div>
          )}
        </div>

        {/* PayPal */}
        <div
          className={`border rounded-xl p-5 cursor-pointer transition-all duration-300 ${selectedMethod === "paypal" ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-blue-300"}`}
          onClick={() => setSelectedMethod("paypal")}
        >
          <div className="flex items-center mb-3">
            <div className={`p-2 rounded-full mr-3 ${selectedMethod === "paypal" ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"}`}>
              <FaPaypal size={20} />
            </div>
            <h3 className="font-medium">PayPal</h3>
          </div>
          {selectedMethod === "paypal" && (
            <div className="text-sm text-gray-600">
              <p>Connect your PayPal account for faster payments</p>
              <button className="mt-3 text-blue-600 text-sm font-medium flex items-center">
                Connect Account <ChevronRight size={16} className="ml-1" />
              </button>
            </div>
          )}
        </div>

        {/* Crypto */}
        <div
          className={`border rounded-xl p-5 cursor-pointer transition-all duration-300 ${selectedMethod === "crypto" ? "border-yellow-500 bg-yellow-50" : "border-gray-200 hover:border-yellow-300"}`}
          onClick={() => setSelectedMethod("crypto")}
        >
          <div className="flex items-center mb-3">
            <div className={`p-2 rounded-full mr-3 ${selectedMethod === "crypto" ? "bg-yellow-100 text-yellow-600" : "bg-gray-100 text-gray-600"}`}>
              <Bitcoin size={20} />
            </div>
            <h3 className="font-medium">Cryptocurrency</h3>
          </div>
          {selectedMethod === "crypto" && (
            <div className="text-sm text-gray-600">
              <p>Receive payments in Bitcoin or Ethereum</p>
              <button className="mt-3 text-yellow-600 text-sm font-medium flex items-center">
                Set Up Wallet <ChevronRight size={16} className="ml-1" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Payment Method Button */}
      <button
        onClick={() => setOpenModal(true)}
        className="mb-8 px-6 py-3 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-colors duration-300 flex items-center"
      >
        <Plus size={20} className="mr-2" />
        Add Payment Method
      </button>

      {/* Transactions and Upcoming Payment */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Transactions */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Recent Transactions</h3>
            <button
              onClick={handleShowTransaction}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
            >
              View All <ChevronRight size={16} className="ml-1" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 text-green-600 rounded-full mr-3">
                  <CheckCircle size={18} />
                </div>
                <div>
                  <p className="font-medium">Payment Received</p>
                  <p className="text-sm text-gray-500">{
                    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(
                      new Date(new Date().getFullYear(), new Date().getMonth() - 1, 15)
                    )
                  }</p>
                </div>
              </div>
              <span className="font-medium text-green-600">+${lastPaidInvoice?.revenue?.toFixed(2) || "0.00"}</span>
            </div>

            <div className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-full mr-3">
                  <Calendar size={18} />
                </div>
                <div>
                  <p className="font-medium">Upcoming Payment</p>
                  <p className="text-sm text-gray-500">{
                    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(
                      new Date(new Date().getFullYear(), new Date().getMonth() + 2, 5)
                    )
                  }</p>
                </div>
              </div>
              <span className="font-medium text-gray-600">${lastUnPaidInvoice?.revenue?.toFixed(2) || "0.00"}</span>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment Details</h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Payment Threshold</span>
              <span className="font-medium">$200.00</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Current Balance</span>
              <span className="font-medium">${lastUnPaidInvoice?.revenue?.toFixed(2) || "0.00"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Next Payout Date</span>
              <span className="font-medium">{
                new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(
                  new Date(new Date().getFullYear(), new Date().getMonth() + 2, 5)
                )
              }</span>
            </div>
            <div className="pt-4 border-t border-gray-200">
              {/* <button className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300">
                Request Early Payout
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={openModal}
        closeModal={() => setOpenModal(false)}
        fetchData={data}
      />
    </div>
  );
};



export default Payment;